import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { motion } from "framer-motion";

const stats = [
  { value: "50+", label: "Projects Delivered" },
  { value: "3", label: "Core Expertise Areas" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "24/7", label: "Support & Maintenance" },
];

const reasons = [
  { title: "Ecommerce, Web & AI Expertise", description: "Deep knowledge across Shopify, Webflow, and AI, so your projects are built by specialists, not generalists." },
  { title: "Scalable & Modern Solutions", description: "We use modern frameworks and best practices to build solutions that grow with your business." },
  { title: "Global Tools & Platforms", description: "Experience working with leading platforms like Shopify, Webflow, Klaviyo, Notion, and more." },
  { title: "Design & Performance First", description: "Clean design paired with performance-driven development: fast, accessible, and built to convert." },
  { title: "Long-Term Partnerships", description: "We're not just a vendor. We build lasting relationships and provide ongoing support as your technology partner." },
];

export function WhyWavai() {
  const { lang } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  return (
    <section className="relative">

      {/* Why us content */}
      <div className="py-12 lg:py-32 bg-surface">
        <div className="container px-6">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            {/* Left — heading */}
            <div className="lg:sticky lg:top-28">
              <motion.p
                className="text-sm uppercase tracking-[0.15em] text-primary font-medium mb-4"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                {t("Why Wavai")}
              </motion.p>
              <motion.h2
                className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 leading-[1.05] text-balance"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.08 }}
              >
                {t("Why Kuwait's best brands")}
                <span className="text-gradient"> {t("choose Wavai")}</span>
              </motion.h2>
              <motion.p
                className="text-muted-foreground text-lg leading-relaxed text-balance"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.16 }}
              >
                {t("A great website isn't just code and pixels — it's how your customers experience your brand. We take time to understand your business, your market and your growth targets, then build the platform that gets you there.")}
              </motion.p>
            </div>

            {/* Right — reasons list */}
            <div className="space-y-4">
              {reasons.map((reason, i) => (
                <motion.div
                  key={reason.title}
                  className="flex gap-4 p-6 rounded-xl bg-card border border-border hover:border-primary/20 transition-all duration-300 hover:shadow-card"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-sans font-medium text-foreground mb-1.5 text-[1.05rem]">{t(reason.title)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(reason.description)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
